import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import Colors from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";
import { useFormik } from "formik";
import { api } from "helpers/api_url";
import React, { Fragment, useEffect, useState } from "react";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import OrderItemTable from "./OrderItemTable";

interface FormValues {
  order_date: string;
  quotation_number: string;
  dispatch_from_warehouse: any;
  customer: any;
  items: any;
  remark: string;
}

const QuotationOrderForm: React.FC = () => {
  document.title = "quotation order form";
  const { quotation_id } = useParams();
  const navigate = useNavigate();
  const [formLoding, setFormLoading] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<any>([]);
  const [dispatchWarehouseOptions, setDispatchWarehouseOptions] = useState<any>(
    []
  );
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [bagQuantityValidation, setBagQuantityValidation] = useState<
    Array<{
      inventory_item_id: string | number;
      available_quantity: number;
      issue_quantity: number;
    }>
  >([]);

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      order_date: new Date().toISOString().split("T")[0],
      quotation_number: "",
      dispatch_from_warehouse: null,
      customer: null,
      items: [],
      remark: "",
    },
    validationSchema: Yup.object({
      order_date: Yup.string().required("Order date is required"),
      dispatch_from_warehouse: Yup.object()
        .nullable()
        .required("Dispatch from warehouse is required"),
      customer: Yup.object().nullable().required("Customer name is required"),
      items: Yup.array()
        .min(1, "At least one item is required")
        .required("At least one item is required"),
    }),
    onSubmit: async (values) => {
      // Validate items before submission
      if (!values.items || values.items.length === 0) {
        formik.setFieldTouched("items", true);
        toast.error("At least one item is required");
        return;
      }

      // Calculate financial summary values from items
      const subtotalBeforeDiscount =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.amount || 0);
        }, 0) || 0;

      const totalItemDiscounts =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.discount_amount || 0);
        }, 0) || 0;

      const netTaxableValue = subtotalBeforeDiscount - totalItemDiscounts;

      const totalGst =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.gst_amount || 0);
        }, 0) || 0;

      const totalAmount =
        values?.items?.reduce((sum: number, item: any) => {
          return sum + parseFloat(item.total_amount || 0);
        }, 0) || 0;

      const payload = {
        // quotation_id: "123e4567-e89b-12d3-a456-426614174000",
        order_date: values?.order_date,
        dispatch_from_warehouse_id:
          values?.dispatch_from_warehouse?.warehouse_id,
        customer_id: values?.customer?.customer_id,
        customer_details: {
          ...values?.customer,
        },
        subtotal_before_discount: parseFloat(subtotalBeforeDiscount.toFixed(2)),
        total_item_discounts: parseFloat(totalItemDiscounts.toFixed(2)),
        net_taxable_value: parseFloat(netTaxableValue.toFixed(2)),
        total_gst: parseFloat(totalGst.toFixed(2)),
        total_amount: parseFloat(totalAmount.toFixed(2)),
        remark: values?.remark,
        items: values?.items?.map((item: any) => ({
          category_id: item?.category?.category_id,
          category_name: item?.category?.category_name,
          sub_category_id: item?.sub_category?.sub_category_id,
          sub_category_name: item?.sub_category?.sub_category_name,
          item_id: item?.item?.item_id,
          item_name: item?.item?.item_name,
          item_code: item?.item?.item_code,
          item_details: {
            ...item?.item,
          },
          unit_id: item?.item?.unit?.unit_id,
          unit_name: item?.unit || item?.item?.unit?.unit_name,
          quantity: parseFloat(item?.quantity) || 0,
          bags:
            item?.bags && Array.isArray(item.bags)
              ? item.bags.map((bag: any) => ({
                  ...bag,
                  available_quantity:
                    typeof bag.available_quantity === "string"
                      ? parseFloat(bag.available_quantity)
                      : bag.available_quantity,
                  issue_quantity:
                    typeof bag.issue_quantity === "string"
                      ? parseFloat(bag.issue_quantity)
                      : bag.issue_quantity,
                }))
              : item?.bags,
          rate: parseFloat(item?.rate) || 0,
          amount: parseFloat(item?.amount) || 0,
          discount: item?.discount ? parseFloat(item?.discount) : 0,
          discount_type:
            item?.discount_type === "%"
              ? "percentage"
              : item?.discount_type === "₹"
              ? "amount"
              : item?.discount_type,
          discount_amount: parseFloat(item?.discount_amount) || 0,
          gst_id: item?.gst?.gst_id,
          gst_percent: item?.gst?.gst_percent
            ? parseFloat(item?.gst?.gst_percent)
            : 0,
          gst_amount: parseFloat(item?.gst_amount) || 0,
          total_amount: parseFloat(item.total_amount) || 0,
          remark: item?.remark,
        })),
      };

      try {
        setFormLoading(true);

        await Axios.post(api?.order?.create, payload);
        toast.success("Order Created Successfully");
        navigate("/order-list");
      } catch (err: any) {
        const backendMessage =
          err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } finally {
        setFormLoading(false);
      }
    },
  });

  const tableValidationSchema = Yup.object().shape({
    category: Yup.object().nullable().required("Category is required"),
    sub_category: Yup.object().nullable().required("Sub Category is required"),
    item: Yup.object().nullable().required("Item Name is required"),
    item_code: Yup.string().trim().required("Item Code is required"),
    unit: Yup.string().trim().required("Unit is required"),
    quantity: Yup.number()
      .typeError("Quantity must be a number")
      .positive("Quantity must be greater than 0")
      .required("Quantity is required"),
    bags: Yup.array()
      .of(
        Yup.object().shape({
          inventory_item_id: Yup.mixed().required(),
          bag_no: Yup.string().required(),
          available_quantity: Yup.number().required(),
          issue_quantity: Yup.number().required(),
        })
      )
      .min(1, "At least one bag is required")
      .required("At least one bag is required"),
    rate: Yup.number()
      .typeError("Rate must be a number")
      .positive("Rate must be greater than 0")
      .required("Rate is required"),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .required("Amount is required"),
    discount: Yup.number()
      .typeError("Discount must be a number")
      .min(0, "Discount cannot be negative")
      .nullable(),
    discount_type: Yup.string()
      .oneOf(["%", "₹"], "Invalid discount type")
      .required("Discount type is required"),
    discount_amount: Yup.number()
      .typeError("Discount amount must be a number")
      .min(0, "Discount amount cannot be negative")
      .nullable(),
    gst: Yup.object().nullable().required("GST is required"),
    gst_amount: Yup.number()
      .typeError("GST amount must be a number")
      .min(0, "GST amount cannot be negative")
      .required("GST amount is required"),
    total_amount: Yup.number()
      .typeError("Total amount must be a number")
      .min(0, "Total amount cannot be negative")
      .required("Total amount is required"),
    remark: Yup.string().trim().nullable(),
  });

  const tableFormik = useFormik<any>({
    enableReinitialize: true,
    initialValues: {
      category: null,
      sub_category: null,
      item: null,
      item_code: "",
      unit: "",
      quantity: "",
      bags: [],
      rate: "",
      amount: "",
      discount: "",
      discount_type: "%",
      discount_amount: "",
      gst: null,
      gst_amount: "",
      total_amount: "",
      remark: "",
    },
    validationSchema: tableValidationSchema,
    onSubmit: async (values) => {
      const payload = {
        ...values,
      };

      // Validate bags
      if (!values.bags || values.bags.length === 0) {
        tableFormik.setFieldTouched("bags", true);
        toast.error("At least one bag is required");
        return;
      }

      // Calculate total issue quantity from all bags
      const totalIssueQuantity = values.bags.reduce((sum: number, bag: any) => {
        const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
        return sum + issueQty;
      }, 0);

      // Validate quantity field is not less than total issue quantity from bags
      const quantity = parseFloat(String(values.quantity)) || 0;
      if (quantity < totalIssueQuantity) {
        tableFormik.setFieldTouched("quantity", true);
        toast.error(
          `Quantity (${quantity}) cannot be less than Total Issue Quantity (${totalIssueQuantity.toFixed(
            2
          )}) from selected bags`
        );
        return;
      }

      const currentItems = formik.values.items || [];

      if (editingItemId !== null) {
        // Update existing item
        const updatedItems = currentItems.map((item: any) =>
          item.id === editingItemId ? { ...payload, id: editingItemId } : item
        );
        formik.setFieldValue("items", updatedItems);
      } else {
        // Add new item
        const newItem = {
          ...payload,
          id: Date.now(), // Temporary ID for the item
        };
        formik.setFieldValue("items", [...currentItems, newItem]);
      }

      // Clear items validation error if it exists
      if (formik.touched.items && formik.errors.items) {
        formik.setFieldError("items", undefined);
      }

      // Reset table formik and editing state after adding/updating item
      tableFormik.resetForm();
      setEditingItemId(null);
    },
  });

  useEffect(() => {
    if (quotation_id) {
      setFormLoading(true);
      Axios.get(`/quotation/${quotation_id}`)
        .then((result: any) => {
          console.log("Result : ", result?.data?.data);
          formik.setFieldValue(
            "quotation_number",
            result?.data?.data?.quotation_number
          );
          // formik.setFieldValue("items", result?.data?.data?.quotation_items)
          console.log(
            "result?.data?.data?.quotation_items : ",
            result?.data?.data?.quotation_items
          );
          const updatedItems = result?.data?.data?.quotation_items?.map(
            (item: any) => ({
              ...item,
              discount_type: item?.discount_type === "percentage" ? "%" : "₹",
              rate: Number(item?.selling_price),
            })
          );

          formik.setFieldValue("items", updatedItems);

          // Initialize bagQuantityValidation with existing bags from all items
          // const initialBagValidation: Array<{
          //     inventory_item_id: string | number;
          //     available_quantity: number;
          //     issue_quantity: number;
          // }> = [];

          // orderItems.forEach((item: any) => {
          //     if (item.bags && Array.isArray(item.bags)) {
          //         item.bags.forEach((bag: any) => {
          //             const inventoryItemId = bag.inventory_item_id;
          //             const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
          //             const availableQty = parseFloat(String(bag.available_quantity)) || 0;

          //             const existingBagIndex = initialBagValidation.findIndex(
          //                 (b) => String(b.inventory_item_id) === String(inventoryItemId)
          //             );

          //             if (existingBagIndex !== -1) {
          //                 // Bag exists, append issue quantity
          //                 initialBagValidation[existingBagIndex].issue_quantity += issueQty;
          //             } else {
          //                 // Bag doesn't exist, add new entry
          //                 initialBagValidation.push({
          //                     inventory_item_id: inventoryItemId,
          //                     available_quantity: availableQty,
          //                     issue_quantity: issueQty,
          //                 });
          //             }
          //         });
          //     }
          // });
        })
        .catch((err) => {
          toast.error("Failed to fetch Quotation data");
        })
        .finally(() => {
          setFormLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    Axios.get(`${api?.master?.customer?.dropdown}`)
      .then((result) => {
        setCustomerOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch customer dropdown");
      });
    Axios.get(`${api?.master?.warehouse?.dropdown}`)
      .then((result) => {
        setDispatchWarehouseOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch warehouse dropdown");
      });
  }, []);

  return (
    <Fragment>
      <div
        className="page-content px-4"
        style={{ backgroundColor: "#e9ecef", minHeight: "100vh" }}
      >
        <Container
          fluid
          style={{
            backgroundColor: "#fff",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 0 15px rgba(0,0,0,0.1)",
          }}
        >
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mb-4">
            <div className="d-flex align-items-center gap-2">
              <Link
                to="/order-list"
                style={{ color: "inherit", textDecoration: "none" }}
              >
                <i
                  className="bi bi-arrow-left"
                  style={{ fontSize: "1.2rem", cursor: "pointer" }}
                  aria-label="Back"
                />
              </Link>
              <h6 className="card-title mb-0">Create Order</h6>
            </div>

            <div className="text-muted">
              <Link
                to="/order-list"
                style={{ color: "inherit", textDecoration: "none" }}
              >
                Order
              </Link>{" "}
              &gt; <span>Create Order</span>
            </div>
          </div>

          <Form autoComplete="off" onSubmit={formik.handleSubmit} noValidate>
            <Row>
              <Col lg={3} className="mb-3">
                <Form.Label>Quotation No.</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Quotation No."
                  name="quotation_number"
                  value={formik.values.quotation_number}
                  onChange={(e) => {
                    formik.setFieldValue("quotation_number", e.target.value);
                  }}
                  onBlur={formik.handleBlur}
                  disabled
                />
              </Col>
              <Col lg={3} className="mb-3">
                <Form.Label>
                  Order Date<span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="order_date"
                  value={formik.values.order_date}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("order_date", value);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.order_date && formik.touched.order_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.order_date}
                </Form.Control.Feedback>
              </Col>
              <Col lg={3} className="mb-3">
                <Form.Label>
                  Dispatch From Warehouse<span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  size="small"
                  options={dispatchWarehouseOptions || []}
                  autoHighlight
                  value={formik.values.dispatch_from_warehouse}
                  onChange={(_, val) => {
                      formik.setFieldValue("dispatch_from_warehouse", val);
                      // Clear all items when warehouse changes
                      formik.setFieldValue("items", []);
                      // Reset table form and editing state
                      tableFormik.resetForm();
                      setEditingItemId(null);
                      // Clear bag quantity validation
                      setBagQuantityValidation([]);
                  }}
                  onBlur={() =>
                    formik.setFieldTouched("dispatch_from_warehouse", true)
                  }
                  getOptionLabel={(option: any) =>
                    option ? option?.warehouse_name : ""
                  }
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) => opt?.warehouse_name || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Warehouse"
                      fullWidth
                      error={
                        formik.touched.dispatch_from_warehouse &&
                        Boolean(formik.errors.dispatch_from_warehouse)
                      }
                      helperText={
                        formik.touched.dispatch_from_warehouse &&
                        typeof formik.errors.dispatch_from_warehouse ===
                          "string"
                          ? formik.errors.dispatch_from_warehouse
                          : undefined
                      }
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor:
                              formik.touched.dispatch_from_warehouse &&
                              formik.errors.dispatch_from_warehouse
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor:
                              formik.touched.dispatch_from_warehouse &&
                              formik.errors.dispatch_from_warehouse
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor:
                              formik.touched.dispatch_from_warehouse &&
                              formik.errors.dispatch_from_warehouse
                                ? "#dc3545"
                                : "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor:
                          formik.touched.dispatch_from_warehouse &&
                          formik.errors.dispatch_from_warehouse
                            ? "#dc3545"
                            : "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor:
                          formik.touched.dispatch_from_warehouse &&
                          formik.errors.dispatch_from_warehouse
                            ? "#dc3545"
                            : "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor:
                          formik.touched.dispatch_from_warehouse &&
                          formik.errors.dispatch_from_warehouse
                            ? "#dc3545"
                            : "#ced4da",
                      },
                    },
                  }}
                />
              </Col>
              <Col lg={3} className="mb-3">
                <Form.Label>
                  Customer Name<span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  size="small"
                  options={customerOptions || []}
                  autoHighlight
                  value={formik.values.customer}
                  onChange={(_, val) => formik.setFieldValue("customer", val)}
                  onBlur={() => formik.setFieldTouched("customer", true)}
                  getOptionLabel={(option: any) =>
                    option ? option?.customer_name : ""
                  }
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) => opt?.customer_name || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Customer"
                      fullWidth
                      error={
                        formik.touched.customer &&
                        Boolean(formik.errors.customer)
                      }
                      helperText={
                        formik.touched.customer &&
                        typeof formik.errors.customer === "string"
                          ? formik.errors.customer
                          : undefined
                      }
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor:
                              formik.touched.customer && formik.errors.customer
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor:
                              formik.touched.customer && formik.errors.customer
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor:
                              formik.touched.customer && formik.errors.customer
                                ? "#dc3545"
                                : "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor:
                          formik.touched.customer && formik.errors.customer
                            ? "#dc3545"
                            : "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor:
                          formik.touched.customer && formik.errors.customer
                            ? "#dc3545"
                            : "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor:
                          formik.touched.customer && formik.errors.customer
                            ? "#dc3545"
                            : "#ced4da",
                      },
                    },
                  }}
                />
              </Col>
            </Row>
            <OrderItemTable
              formik={tableFormik}
              mainFormik={formik}
              editingItemId={editingItemId}
              setEditingItemId={setEditingItemId}
              bagQuantityValidation={bagQuantityValidation}
              setBagQuantityValidation={setBagQuantityValidation}
              warehouseId={formik.values.dispatch_from_warehouse?.warehouse_id}
            />
            {formik.touched.items && formik.errors.items && (
              <div
                className="text-danger mb-3"
                style={{ fontSize: "0.875rem", marginTop: "-15px" }}
              >
                {typeof formik.errors.items === "string"
                  ? formik.errors.items
                  : "At least one item is required"}
              </div>
            )}

            {/* Remarks and Financial Summary Section */}
            <Row className="mb-4">
              <Col lg={7} className="mb-3">
                <div
                  style={{
                    border: `1px solid ${Colors?.grey}`,
                    borderRadius: "4px",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-2 mb-0 py-2 px-1"
                    style={{ background: Colors?.grey }}
                  >
                    <i
                      className="bi bi-chevron-down"
                      style={{ fontSize: "0.675rem", color: "#000" }}
                    ></i>
                    <h6 className="mb-0">Remark</h6>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="remark"
                    placeholder="Enter your remark"
                    value={formik.values.remark}
                    onChange={(e) =>
                      formik.setFieldValue("remark", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    style={{
                      border: "1px solid #transparant",
                      borderRadius: "0px",
                      resize: "vertical",
                    }}
                  />
                </div>
              </Col>
              <Col lg={5} className="mb-3">
                <div
                  style={{
                    backgroundColor: "#e3f2fd",
                    padding: "20px",
                    borderRadius: "8px",
                    border: "1px solid #90caf9",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                      Subtotal (Before Discount):
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#212529",
                      }}
                    >
                      ₹{" "}
                      {formik.values.items
                        ?.reduce((sum: number, item: any) => {
                          return sum + parseFloat(item.amount || 0);
                        }, 0)
                        .toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                      Total Item Discounts:
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#212529",
                      }}
                    >
                      ₹{" "}
                      {formik.values.items
                        ?.reduce((sum: number, item: any) => {
                          return sum + parseFloat(item.discount_amount || 0);
                        }, 0)
                        .toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                      Net Taxable Value:
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#212529",
                      }}
                    >
                      ₹{" "}
                      {(
                        formik.values.items?.reduce(
                          (sum: number, item: any) => {
                            return sum + parseFloat(item.amount || 0);
                          },
                          0
                        ) -
                        formik.values.items?.reduce(
                          (sum: number, item: any) => {
                            return sum + parseFloat(item.discount_amount || 0);
                          },
                          0
                        )
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "0.875rem", color: "#212529" }}>
                      Total GST:
                    </span>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#212529",
                      }}
                    >
                      ₹{" "}
                      {formik.values.items
                        ?.reduce((sum: number, item: any) => {
                          return sum + parseFloat(item.gst_amount || 0);
                        }, 0)
                        .toFixed(2)}
                    </span>
                  </div>
                  <hr style={{ margin: "12px 0", borderColor: "#90caf9" }} />
                  <div className="d-flex justify-content-between align-items-center">
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "600",
                        color: "#212529",
                      }}
                    >
                      Total Amount:
                    </span>
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "700",
                        color: "#212529",
                      }}
                    >
                      ₹{" "}
                      {formik.values.items
                        ?.reduce((sum: number, item: any) => {
                          return sum + parseFloat(item.total_amount || 0);
                        }, 0)
                        .toFixed(2)}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-3 py-3">
              <Button
                variant="light"
                onClick={() => navigate(-1)}
                style={{
                  backgroundColor: "white",
                  border: "1px solid #ced4da",
                  color: "#212529",
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                style={{
                  background: Colors?.themeBgColor,
                  borderColor: Colors?.themeBgColor,
                  color: "#FFF",
                  minWidth: "70px",
                }}
              >
                {!formLoding ? (
                  "Submit"
                ) : (
                  <Spinner animation="border" size="sm" />
                )}
              </Button>
            </div>
          </Form>
        </Container>
      </div>
    </Fragment>
  );
};

export default QuotationOrderForm;
